import React, { useState } from 'react';
import { DSBadge, DSIcon, searchFieldProps } from '../design-system/primitives';
import UserAvatar from './UserAvatar';

// Shared dark, warm-accented maintenance-workflow UI primitives, ported from
// the Machine Maintenance wireframe (Mobile UI for Ticket Tiering /
// MaintenanceScreens.tsx + maintenanceData.ts). Scoped under .maintenance-ui
// (add that class to a consuming page's root element) - this codebase bundles
// all CSS globally, so bare class names collide across files. Deliberately a
// separate primitives file from TicketUi.js/.css rather than reusing it: this
// feature uses a warmer green accent instead of the ticket system's red/amber
// urgency treatment (design prompt, "Visual tone").

export function relTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor(diff / 3600000);
  const minutes = Math.floor(diff / 60000);
  if (days > 6) return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  return `${Math.max(minutes, 0)}m ago`;
}

// missedCycles is null for a machine that's never had a completed record -
// treated as maximally overdue (a judgment call, see MACHINE_MAINTENANCE
// implementation plan) rather than excluded or shown as "on time".
export function overdueConfig(missedCycles, neverCompleted) {
  if (neverCompleted) {
    return { label: 'Never serviced', color: '#ff6b6b', bg: '#2a0e0e', border: '#7f1d1d' };
  }
  if (!missedCycles || missedCycles <= 0) {
    return { label: 'Due this cycle', color: '#22c55e', bg: '#0c1f14', border: '#1a5c35' };
  }
  if (missedCycles === 1) {
    return { label: '1 cycle overdue', color: '#ffb020', bg: '#271c06', border: '#7c4a00' };
  }
  return { label: `${missedCycles} cycles overdue`, color: '#ff6b6b', bg: '#2a0e0e', border: '#7f1d1d' };
}

export function ProgressBar({ done, total, complete }) {
  // `complete` can be true even when done/total describe a stray/redo record
  // that doesn't reflect the completed cycle (e.g. a machine completed this
  // interval that also has an unrelated newly-opened record at 0 items) - the
  // fill always matches the label rather than a mismatched count.
  const pct = complete ? 100 : total > 0 ? (done / total) * 100 : 0;
  return (
    <div className="mu-progress-bar">
      <div className="mu-progress-bar__track">
        <div
          className="mu-progress-bar__fill"
          style={{ width: `${pct}%`, background: complete ? '#22c55e' : '#14b87e' }}
        />
      </div>
      <span className={`mu-progress-bar__label ${complete ? 'mu-progress-bar__label--complete' : ''}`}>
        {complete ? <><DSIcon name="check" /> Done</> : total > 0 ? `${done}/${total}` : 'Not started'}
      </span>
    </div>
  );
}

export function AssignStatePill({ assignedToName, isAssignedToViewer, selfClaimedByName, isSelfClaimedByViewer }) {
  if (assignedToName) {
    return (
      <span className={`mu-assign-pill ${isAssignedToViewer ? 'mu-assign-pill--you-assigned' : 'mu-assign-pill--other'}`}>
        <span className="mu-assign-pill__dot" />
        {isAssignedToViewer ? 'Assigned to you' : `Assigned: ${assignedToName}`}
      </span>
    );
  }
  if (selfClaimedByName) {
    return (
      <span className={`mu-assign-pill ${isSelfClaimedByViewer ? 'mu-assign-pill--you-claimed' : 'mu-assign-pill--other'}`}>
        <span className="mu-assign-pill__dot" />
        {isSelfClaimedByViewer ? "You're working this" : `${selfClaimedByName} is on this`}
      </span>
    );
  }
  return <span className="mu-assign-pill mu-assign-pill--empty">Unclaimed</span>;
}

// Shared row for both the needs-maintenance List and the Organizer Dashboard
// (design prompt §3: "Can reuse the row component from Screen 1"). `rightAction`
// lets the Dashboard inject its Assign/Reassign button without forking the row.
export function MaintenanceRow({ machine, viewerId, onPress, rightAction }) {
  const total = machine.total_items;
  const done = machine.completed_items;
  // Complete-this-cycle must come from the backend's needs_work flag, not be
  // re-derived from has_record/total/done - those fields only describe the
  // *open* record and collapse to false/0/0 the moment the last item on a
  // record is completed, which would otherwise make a just-finished machine
  // look unstarted.
  const isComplete = !machine.needs_work;
  const oc = overdueConfig(machine.missed_cycles, machine.never_completed);
  const isAssignedToViewer = machine.assigned_to === viewerId;
  const isSelfClaimedByViewer = machine.self_claimed_by === viewerId;

  return (
    <div
      className="mu-row"
      role="button"
      tabIndex={0}
      onClick={() => onPress(machine)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPress(machine);
        }
      }}
    >
      <div className="mu-row__photo">
        {machine.machine_image_url ? (
          <img src={machine.machine_image_url} alt={machine.machine_name} className="mu-row__photo-img" />
        ) : (
          <span className="mu-row__photo-fallback"><DSIcon name="gamepad" /></span>
        )}
      </div>
      <div className="mu-row__content">
        <div className="mu-row__top">
          <p className="mu-row__name">{machine.machine_name}</p>
          {rightAction ? (
            // rightAction (e.g. the Dashboard's Assign/Reassign button) is a
            // sibling action, not part of the row's own "open this machine"
            // press target - stop its click from bubbling to the row onClick
            // above, or pressing it would navigate to the checklist instead
            // of doing its own thing.
            <span onClick={(e) => e.stopPropagation()}>{rightAction}</span>
          ) : isComplete ? (
            // A machine whose current record is fully checked off shouldn't
            // show the not-yet-overdue "Due this cycle" badge - same green as
            // this, but its wording reads as still-needing-work, which
            // contradicts the "✓ Done" the progress bar right below it
            // already shows (e.g. in the Organizer Dashboard's "Complete This
            // Cycle" section).
            <DSBadge variant="success" size="sm" leadingIcon={<DSIcon name="check" />}>Complete</DSBadge>
          ) : (
            <span className="mu-row__overdue-badge" style={{ background: oc.bg, borderColor: oc.border, color: oc.color }}>
              {oc.label}
            </span>
          )}
        </div>
        <ProgressBar done={done} total={total} complete={isComplete} />
        <div className="mu-row__bottom">
          <AssignStatePill
            assignedToName={machine.assigned_to_name}
            isAssignedToViewer={isAssignedToViewer}
            selfClaimedByName={machine.self_claimed_by_name}
            isSelfClaimedByViewer={isSelfClaimedByViewer}
          />
          <span className="mu-row__last-completed">
            {machine.never_completed
              ? 'Never completed'
              : `Last completed ${relTime(machine.last_completed_at)}${machine.last_completed_by ? ` by ${machine.last_completed_by}` : ''}`}
          </span>
        </div>
      </div>
    </div>
  );
}

// Read-only log of every past Maintenance Record for a machine - each cycle's
// record, expandable to its per-item completion/notes. Shared between the
// Maintenance Record page's own "History" section (past records alongside
// the current interactive checklist) and the Machine Detail page's
// Maintenance tab (the full log with no interactive checklist alongside it).
export function MaintenanceHistoryList({ records, viewerId, emptyMessage = 'No maintenance history yet.' }) {
  const [expandedIds, setExpandedIds] = useState(new Set());

  if (!records || records.length === 0) {
    return <p className="mu-history-empty">{emptyMessage}</p>;
  }

  const toggle = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="mu-history">
      {records.map((record) => {
        const expanded = expandedIds.has(record.id);
        const items = record.items || [];
        const done = items.filter((i) => i.completed_at).length;

        return (
          <div key={record.id} className="mu-history__record">
            <button type="button" className="mu-history__record-header" onClick={() => toggle(record.id)}>
              <span className="mu-history__record-title">
                {record.completed_at
                  ? `Completed ${new Date(record.completed_at).toLocaleDateString()}`
                  : `Started ${new Date(record.created_at).toLocaleDateString()}, not completed`}
              </span>
              <span className="mu-history__record-meta">{done}/{items.length} items</span>
              <span className={`mu-history__chevron ${expanded ? 'mu-history__chevron--open' : ''}`}><DSIcon name="chevronDown" /></span>
            </button>

            {expanded && (
              <div className="mu-history__items">
                {items.map((item) => (
                  <div key={item.id} className="mu-history__item">
                    <span className={`mu-history__item-check ${item.completed_at ? 'mu-history__item-check--done' : ''}`}>
                      {item.completed_at ? <DSIcon name="check" /> : ''}
                    </span>
                    <div className="mu-history__item-body">
                      <p className="mu-history__item-title">{item.title}</p>
                      {item.completed_at ? (
                        <p className="mu-history__item-meta">
                          {item.completed_by === viewerId ? 'You' : item.completed_by_name} · {new Date(item.completed_at).toLocaleDateString()}
                        </p>
                      ) : (
                        <p className="mu-history__item-meta">Not completed</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Collapsible section wrapper for grouped row lists (e.g. the Organizer
// Dashboard's Needs Maintenance / In Progress / Complete buckets). Defaults
// open so nothing is hidden on first load; state resets per-mount, not
// persisted - collapsing is a read-time convenience, not a saved preference.
export function CollapsibleSection({ title, count, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="mu-collapsible">
      <button type="button" className="mu-collapsible__header" onClick={() => setOpen((prev) => !prev)}>
        <span className="mu-collapsible__title">
          {title}
          {typeof count === 'number' && <span className="mu-collapsible__count">{count}</span>}
        </span>
        <span className={`mu-collapsible__chevron ${open ? 'mu-collapsible__chevron--open' : ''}`}><DSIcon name="chevronDown" /></span>
      </button>
      {open && <div className="mu-collapsible__body">{children}</div>}
    </div>
  );
}

// Organizer's assign/reassign bottom sheet. Shared by the Organizer Dashboard
// (assign from the machine list) and the Maintenance Record page (assign while
// looking at the checklist itself) so both offer the same picker instead of
// the record page growing a second, subtly different one.
export function MaintenanceAssignSheet({ machineName, users, loading, currentAssignedTo, busy, onAssign, onClose }) {
  const [search, setSearch] = useState('');

  // Club membership runs ~400 people - scrolling a flat list to find one name
  // doesn't work at that size, so the sheet filters by name/username as you type.
  const q = search.trim().toLowerCase();
  const filtered = !q ? users : users.filter((u) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase();
    return name.includes(q) || (u.username || '').toLowerCase().includes(q);
  });

  return (
    <div className="mu-sheet-overlay" onClick={onClose}>
      <div className="mu-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="mu-sheet__header">
          <div>
            <p className="mu-sheet__title">Assign - {machineName}</p>
            <p className="mu-sheet__subtitle">Pick one person for the whole record. Any tier - Organizer's call.</p>
          </div>
          <button type="button" className="mu-sheet__close" onClick={onClose}>×</button>
        </div>
        <div className="mu-sheet__search">
          <input
            {...searchFieldProps}
            className="mu-sheet__search-input"
            placeholder="Search members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="mu-sheet__list">
          {currentAssignedTo && (
            <button type="button" className="mu-sheet__item mu-sheet__item--remove" onClick={() => onAssign(null)} disabled={busy}>
              <span className="mu-sheet__item-icon mu-sheet__item-icon--remove">✕</span>
              Remove assignment
            </button>
          )}
          {loading && <p className="mu-sheet__no-results">Loading members…</p>}
          {filtered.map((u) => {
            const isCurrent = currentAssignedTo === u.id;
            const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username;
            return (
              <button
                type="button"
                key={u.id}
                className={`mu-sheet__item ${isCurrent ? 'mu-sheet__item--current' : ''}`}
                onClick={() => onAssign(u.id)}
                disabled={busy}
              >
                <UserAvatar person={u} className="mu-sheet__item-avatar" />
                <span className="mu-sheet__item-info">
                  <span className="mu-sheet__item-name">{name}</span>
                  <span className="mu-sheet__item-tier">Tier {u.tech_tier}{u.role === 'admin' ? ' - Admin' : ''}</span>
                </span>
                {isCurrent && <span className="mu-sheet__item-current-tag">current</span>}
              </button>
            );
          })}
          {!loading && filtered.length === 0 && (
            <p className="mu-sheet__no-results">
              {q ? `No members match "${search}".` : 'No assignable members found.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function MaintenanceActionBtn({ label, variant = 'default', onClick, disabled, type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`mu-action-btn mu-action-btn--${variant}`}
    >
      {label}
    </button>
  );
}
